import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plug, Key, Webhook as WebhookIcon, CheckCircle2, XCircle, Copy, Trash2, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENTS = [
  { k: 'excedente.creado',     label: 'Excedente creado' },
  { k: 'pedido.confirmado',    label: 'Pedido confirmado' },
  { k: 'subasta.finalizada',   label: 'Subasta finalizada' },
  { k: 'stock.actualizado',    label: 'Stock actualizado' },
];

const relTime = (iso) => {
  if (!iso) return 'Nunca';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'hace instantes';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
};

const Integrations = () => {
  const nav = useNavigate();
  const { getAuthHeader } = useAuth();
  const auth = { headers: { Authorization: getAuthHeader() } };

  const [items, setItems] = useState([]);
  const [connecting, setConnecting] = useState(null); // provider object being connected
  const [connectCreds, setConnectCreds] = useState({});
  const [connectAutoSync, setConnectAutoSync] = useState(true);
  const [testingProvider, setTestingProvider] = useState(null);

  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState({ read: true, write: false });
  const [createdKey, setCreatedKey] = useState(null); // { key, name } shown once

  const [hooks, setHooks] = useState([]);
  const [newHookUrl, setNewHookUrl] = useState('');
  const [newHookEvents, setNewHookEvents] = useState(new Set());
  const [createdHook, setCreatedHook] = useState(null); // { secret, url, events }

  const load = useCallback(async () => {
    try {
      const [i, k, w] = await Promise.all([
        axios.get(`${API}/integrations`, auth),
        axios.get(`${API}/api-keys`, auth),
        axios.get(`${API}/webhooks`, auth),
      ]);
      setItems(i.data); setApiKeys(k.data); setHooks(w.data);
    } catch (e) {
      toast.error('No se pudo cargar Integraciones');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---------- Integrations ----------

  const openConnect = (prov) => {
    setConnecting(prov);
    setConnectCreds({});
    setConnectAutoSync(true);
  };

  const doTestPing = async (prov, creds) => {
    setTestingProvider(prov.provider);
    try {
      const { data } = await axios.post(`${API}/integrations/${prov.provider}/test`, { credentials: creds }, auth);
      if (data.ok) toast.success(`Conexión OK (${data.latency_ms} ms)`);
      else toast.error(data.error || 'Fallo la conexión');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fallo la conexión');
    } finally {
      setTestingProvider(null);
    }
  };

  const doConnect = async () => {
    if (!connecting) return;
    try {
      await axios.post(`${API}/integrations/${connecting.provider}/connect`, {
        credentials: connectCreds,
        auto_sync: connectAutoSync,
      }, auth);
      toast.success(`${connecting.name} conectado`);
      setConnecting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo conectar');
    }
  };

  const toggleAutoSync = async (prov, value) => {
    try {
      await axios.patch(`${API}/integrations/${prov.provider}/sync`, { auto_sync: value }, auth);
      load();
    } catch (e) { toast.error('No se pudo actualizar'); }
  };

  const disconnect = async (prov) => {
    if (!window.confirm(`¿Desconectar ${prov.name}?`)) return;
    await axios.delete(`${API}/integrations/${prov.provider}`, auth);
    toast.info(`${prov.name} desconectado`);
    load();
  };

  // ---------- API Keys ----------

  const createApiKey = async () => {
    if (!newKeyName.trim()) { toast.error('Ponle nombre a la clave'); return; }
    const scopes = Object.keys(newKeyScopes).filter((k) => newKeyScopes[k]);
    if (scopes.length === 0) { toast.error('Selecciona al menos un permiso'); return; }
    const { data } = await axios.post(`${API}/api-keys`, { name: newKeyName.trim(), scopes }, auth);
    setCreatedKey(data);
    setNewKeyName('');
    setNewKeyScopes({ read: true, write: false });
    load();
  };

  const revokeApiKey = async (id) => {
    if (!window.confirm('¿Revocar esta API Key? La aplicación cliente dejará de funcionar de inmediato.')) return;
    await axios.post(`${API}/api-keys/${id}/revoke`, {}, auth);
    toast.info('API Key revocada');
    load();
  };

  // ---------- Webhooks ----------

  const createWebhook = async () => {
    if (!newHookUrl.trim()) { toast.error('Indica la URL del webhook'); return; }
    if (newHookEvents.size === 0) { toast.error('Selecciona al menos un evento'); return; }
    try {
      const { data } = await axios.post(`${API}/webhooks`, { url: newHookUrl.trim(), events: [...newHookEvents] }, auth);
      setCreatedHook(data);
      setNewHookUrl('');
      setNewHookEvents(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear el webhook');
    }
  };

  const deleteHook = async (id) => {
    if (!window.confirm('¿Eliminar webhook?')) return;
    await axios.delete(`${API}/webhooks/${id}`, auth);
    load();
  };

  const testHook = async (id) => {
    try {
      const { data } = await axios.post(`${API}/webhooks/${id}/test`, {}, auth);
      toast.success(`Ping enviado (HTTP ${data.status_code})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ping fallido');
    }
  };

  const toggleHookActive = async (h) => {
    await axios.patch(`${API}/webhooks/${h.id}`, { active: !h.active }, auth);
    load();
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copiado al portapapeles'); };

  // ---------- Render ----------

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => nav('/dashboard')} data-testid="back-button">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">Integraciones y API</h1>
            <p className="text-xs text-slate-500">Sincroniza stock y pedidos con tu POS/ERP o tu software propio</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <Tabs defaultValue="pos" className="w-full">
          <TabsList data-testid="integrations-tabs">
            <TabsTrigger value="pos" data-testid="tab-pos"><Plug size={14} className="mr-1" />Software externo</TabsTrigger>
            <TabsTrigger value="keys" data-testid="tab-keys"><Key size={14} className="mr-1" />API Keys</TabsTrigger>
            <TabsTrigger value="hooks" data-testid="tab-hooks"><WebhookIcon size={14} className="mr-1" />Webhooks</TabsTrigger>
          </TabsList>

          {/* ====== POS/ERP ====== */}
          <TabsContent value="pos" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pos-grid">
              {items.map((prov) => (
                <div key={prov.provider} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={`pos-card-${prov.provider}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{prov.logo}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{prov.name}</p>
                      {prov.description && (
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{prov.description}</p>
                      )}
                      {prov.connected ? (
                        <p className="text-xs text-emerald-700 flex items-center gap-1 mt-1"><CheckCircle2 size={12} /> Conectado</p>
                      ) : (
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><XCircle size={12} /> Desconectado</p>
                      )}
                      {prov.connected && (
                        <p className="text-[11px] text-slate-400 mt-1">Última sync: {relTime(prov.last_sync_at)}</p>
                      )}
                    </div>
                  </div>
                  {prov.connected ? (
                    <div className="mt-3 space-y-2">
                      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`autosync-${prov.provider}`}
                          checked={prov.auto_sync}
                          onChange={(e) => toggleAutoSync(prov, e.target.checked)}
                          className="h-4 w-4 accent-emerald-600"
                        />
                        Sincronizar stock automáticamente
                      </label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => disconnect(prov)}
                        className="w-full text-red-700 border-red-200 hover:bg-red-50"
                        data-testid={`disconnect-${prov.provider}`}
                      >
                        Desconectar
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openConnect(prov)}
                      data-testid={`connect-${prov.provider}`}
                    >
                      Conectar
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-4">
              Estas integraciones conectan tu ERP o sistema de gestión de almacén con RestoSupply para sincronizar <strong>inventario</strong>, publicar <strong>lotes de excedentes</strong> y volcar los <strong>pedidos de venta</strong> generados en la plataforma. La conexión con Sage, SAP Business One, Dynamics 365 (Navision), Odoo, A3ERP y Silco requiere las credenciales facilitadas por cada proveedor y actualmente se ejecuta en <strong>modo simulado</strong> hasta activar el conector definitivo con cada casa de software.
            </p>
          </TabsContent>

          {/* ====== API KEYS ====== */}
          <TabsContent value="keys" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">Crear nueva API Key</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                  <Label className="text-xs text-slate-500">Nombre</Label>
                  <Input
                    data-testid="new-key-name"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="Integración con mi ERP"
                    className="h-9"
                  />
                </div>
                <div className="flex gap-4 items-center pt-4">
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" data-testid="scope-read" checked={newKeyScopes.read} onChange={(e) => setNewKeyScopes({ ...newKeyScopes, read: e.target.checked })} className="h-4 w-4 accent-emerald-600" />
                    Lectura
                  </label>
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" data-testid="scope-write" checked={newKeyScopes.write} onChange={(e) => setNewKeyScopes({ ...newKeyScopes, write: e.target.checked })} className="h-4 w-4 accent-emerald-600" />
                    Escritura
                  </label>
                </div>
                <Button onClick={createApiKey} data-testid="create-api-key" className="bg-emerald-600 hover:bg-emerald-700 text-white h-9">
                  Crear API Key
                </Button>
              </div>
            </div>

            <div className="mt-4 bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
              {apiKeys.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center" data-testid="no-keys">No hay API Keys creadas.</p>
              ) : apiKeys.map((k) => (
                <div key={k.id} className="p-4 flex items-center gap-3" data-testid={`api-key-${k.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{k.name} {k.revoked && <span className="ml-1 text-[10px] font-bold text-red-700">REVOCADA</span>}</p>
                    <p className="text-xs text-slate-500 font-mono">{k.prefix}••••••••</p>
                    <p className="text-[11px] text-slate-400">
                      Permisos: {k.scopes.join(', ')} · Creada {relTime(k.created_at)}
                    </p>
                  </div>
                  {!k.revoked && (
                    <Button size="sm" variant="outline" onClick={() => revokeApiKey(k.id)} data-testid={`revoke-${k.id}`} className="text-red-700 border-red-200 hover:bg-red-50">
                      <Trash2 size={14} className="mr-1" /> Revocar
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 bg-slate-100 rounded-lg p-3 flex items-center justify-between">
              <p className="text-xs text-slate-600">Consulta la referencia de la API</p>
              <a href={`${BACKEND_URL}/docs`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-emerald-700 hover:underline flex items-center gap-1" data-testid="swagger-link">
                Swagger <ExternalLink size={12} />
              </a>
            </div>
          </TabsContent>

          {/* ====== WEBHOOKS ====== */}
          <TabsContent value="hooks" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">Registrar nuevo Webhook</h2>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">URL de destino</Label>
                  <Input
                    data-testid="new-hook-url"
                    value={newHookUrl}
                    onChange={(e) => setNewHookUrl(e.target.value)}
                    placeholder="https://tu-erp.com/hooks/restosupply"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Eventos a recibir</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {EVENTS.map((ev) => (
                      <label key={ev.k} className="flex items-center gap-2 text-sm border border-slate-200 rounded p-2 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`hook-event-${ev.k}`}
                          className="h-4 w-4 accent-emerald-600"
                          checked={newHookEvents.has(ev.k)}
                          onChange={(e) => {
                            const s = new Set(newHookEvents);
                            if (e.target.checked) s.add(ev.k); else s.delete(ev.k);
                            setNewHookEvents(s);
                          }}
                        />
                        <span className="text-xs">{ev.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button onClick={createWebhook} data-testid="create-webhook" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  Crear webhook
                </Button>
              </div>
            </div>

            <div className="mt-4 bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
              {hooks.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center" data-testid="no-hooks">No tienes webhooks configurados.</p>
              ) : hooks.map((h) => (
                <div key={h.id} className="p-4" data-testid={`webhook-${h.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono truncate text-slate-900">{h.url}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Eventos: {h.events.join(', ')}</p>
                      {h.last_delivery_at && (
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Último envío {relTime(h.last_delivery_at)}
                          {h.last_status_code && <span> · HTTP {h.last_status_code}</span>}
                          {h.last_error && <span className="text-red-700"> · {h.last_error}</span>}
                        </p>
                      )}
                    </div>
                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                      <input type="checkbox" checked={h.active} onChange={() => toggleHookActive(h)} className="h-4 w-4 accent-emerald-600" data-testid={`hook-active-${h.id}`} />
                      Activo
                    </label>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => testHook(h.id)} data-testid={`test-hook-${h.id}`}>
                      <RefreshCw size={13} className="mr-1" /> Probar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteHook(h.id)} className="text-red-700 border-red-200 hover:bg-red-50" data-testid={`delete-hook-${h.id}`}>
                      <Trash2 size={13} className="mr-1" /> Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Connect modal */}
      <Dialog open={!!connecting} onOpenChange={(o) => !o && setConnecting(null)}>
        <DialogContent data-testid="connect-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Conectar {connecting?.name}</DialogTitle>
            <DialogDescription>Introduce las credenciales que te haya facilitado el proveedor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {connecting?.credential_fields?.map((field) => (
              <div key={field}>
                <Label className="text-xs text-slate-500 capitalize">{field.replace(/_/g, ' ')}</Label>
                <Input
                  data-testid={`cred-${field}`}
                  value={connectCreds[field] || ''}
                  onChange={(e) => setConnectCreds({ ...connectCreds, [field]: e.target.value })}
                  className="h-9"
                />
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={connectAutoSync} onChange={(e) => setConnectAutoSync(e.target.checked)} className="h-4 w-4 accent-emerald-600" data-testid="connect-autosync" />
              Sincronizar stock automáticamente
            </label>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => doTestPing(connecting, connectCreds)} disabled={testingProvider === connecting?.provider} data-testid="test-ping-button">
                {testingProvider === connecting?.provider ? 'Probando…' : 'Probar conexión'}
              </Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doConnect} data-testid="confirm-connect">
                Conectar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Created API Key (shown once) */}
      <Dialog open={!!createdKey} onOpenChange={(o) => !o && setCreatedKey(null)}>
        <DialogContent data-testid="new-key-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>API Key creada</DialogTitle>
            <DialogDescription className="text-red-700">
              Cópiala ahora — no volveremos a mostrarla completa.
            </DialogDescription>
          </DialogHeader>
          {createdKey && (
            <div className="space-y-2">
              <p className="text-sm text-slate-700">{createdKey.name} · <span className="text-slate-500">{createdKey.scopes.join(', ')}</span></p>
              <div className="flex items-center gap-2 bg-slate-100 rounded p-2 font-mono text-xs break-all">
                <span className="flex-1" data-testid="created-key-value">{createdKey.key}</span>
                <Button size="sm" variant="outline" onClick={() => copy(createdKey.key)} data-testid="copy-key"><Copy size={12} /></Button>
              </div>
              <Button className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setCreatedKey(null)}>Ya la he guardado</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Created Webhook secret */}
      <Dialog open={!!createdHook} onOpenChange={(o) => !o && setCreatedHook(null)}>
        <DialogContent data-testid="new-hook-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Webhook registrado</DialogTitle>
            <DialogDescription className="text-red-700">
              Guarda el secreto — se usa para validar la firma HMAC. No lo volveremos a mostrar.
            </DialogDescription>
          </DialogHeader>
          {createdHook && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 font-mono truncate">{createdHook.url}</p>
              <div className="flex items-center gap-2 bg-slate-100 rounded p-2 font-mono text-xs break-all">
                <span className="flex-1" data-testid="created-hook-secret">{createdHook.secret}</span>
                <Button size="sm" variant="outline" onClick={() => copy(createdHook.secret)}><Copy size={12} /></Button>
              </div>
              <Button className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setCreatedHook(null)}>Guardado</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Integrations;
